import React from "react";
import { Cue } from "../../shared/interfaces/GameLogicTypes";
import { renderCueContent } from "../../shared/helper/renderCueContent";
import PlayerAvatar from "../PlayerAvatar/PlayerAvatar";
import DistributionCurve from "./DistributionCurve";
import useUiScale, { useFitScale } from "../../shared/helper/useUiScale";
import "./VersusBreakdown.css";

// Above this many players the avatar grid stops being readable — five columns of overlapping
// heads with a comma-joined name list under each. Past it the row shows the distribution instead.
const AVATAR_LIMIT = 10;
// Panel surface, for the ring that keeps the curve's dots legible where they cross the line.
const SURFACE = "#FAF9F7";

// Same palette as SequenceComparison / getColorStylebyNumber so a cue keeps its colour everywhere.
const CUE_HEX = ["#fff355", "#608ad6", "#e75151", "#92dc67", "#e17d47"];
const CUE_FG = ["#333333", "#ffffff", "#ffffff", "#ffffff", "#ffffff"];
const RANK_COLORS = ["#FFD700", "#B0B0B0", "#CD7F32", "#555555", "#c0392b"];
const RANK_STROKES = ["#8a7100", "#505050", "#7a4c1e", "#222222", "#7a1010"];

export interface BreakdownPlayer {
  id: string;
  name: string;
  color: string;
  /** The player's own ranking: cue numbers, best first. */
  sequence: number[];
}

export interface VersusBreakdownProps {
  /** The group's aggregate ranking — cue numbers, best first. Drives the row order. */
  groupRanking: number[];
  cues: Cue[];
  players: BreakdownPlayer[];
  /** Highlighted with a ring so you can find yourself at a glance. */
  viewerId?: string;
  mobile?: boolean;
}

// Five option rows at scale 1, and what else the results page needs beside them (question header,
// paper padding, the see-mine / ready button row). Drives useFitScale.
const DESKTOP_NATURAL_H = 460;
// The curve rows are a little taller than an avatar row, so they get their own target.
const CURVE_NATURAL_H = 550;
const DESKTOP_RESERVED_H = 250;

/** Bar text shrinks with length instead of truncating — answers are never cut off. */
function barFontPx(length: number, mobile: boolean): number {
  const scale = mobile ? [15, 13.5, 12, 10.5] : [15.5, 13.5, 12, 10.5];
  if (length <= 18) return scale[0];
  if (length <= 32) return scale[1];
  if (length <= 52) return scale[2];
  return scale[3];
}

/**
 * Versus "all results": one bar per option in the group's order, and under each bar the players
 * who put that option in each of the five slots.
 *
 * Reading a wall of five-card sequences tells you little; what's interesting in a group round is
 * who agreed with the group on a given option and who was miles off. So the option is the row and
 * the players are the marks.
 */
export default function VersusBreakdown({
  groupRanking,
  cues,
  players,
  viewerId,
  mobile,
}: VersusBreakdownProps) {
  const ranked = players.filter((p) => p.sequence && p.sequence.length > 0);
  const slots = [1, 2, 3, 4, 5];
  const asCurve = ranked.length > AVATAR_LIMIT;

  const uiScale = useUiScale();
  // Grows on large displays, shrinks again on a viewport shorter than 1080p (see useFitScale).
  const fit = useFitScale(
    asCurve ? CURVE_NATURAL_H : DESKTOP_NATURAL_H,
    DESKTOP_RESERVED_H
  );
  const scale = mobile ? uiScale : uiScale * fit;
  const avatarPx = Math.round((mobile ? 26 : 29) * scale);
  // Tall enough that the difference between "the group agreed" and "the group split" is a shape
  // you read at a glance rather than a ripple.
  const curveH = Math.round((mobile ? 60 : 66) * scale);

  // Individual names are gone in the curve view, so the viewer's own pick keeps the self-ring the
  // avatars carry — it is the one identity that still matters at this lobby size.
  const viewerSequence = ranked.find((p) => p.id === viewerId)?.sequence;
  const viewerSlotOf = (cueNumber: number) => {
    const at = viewerSequence ? viewerSequence.indexOf(cueNumber) : -1;
    return at === -1 ? undefined : at + 1;
  };

  // A column is a fifth of the row, so a big cluster has to tuck in tighter or it would push the
  // grid wider than the panel.
  const overlapFor = (count: number) =>
    Math.round(avatarPx * (count >= 4 ? 0.62 : count === 3 ? 0.5 : 0.34));

  return (
    <div
      className={"vb-root" + (mobile ? " vb-root--mobile" : "")}
      style={{ ["--vb-scale" as any]: mobile ? 1 : fit }}
    >
      {groupRanking.map((cueNumber, rowIndex) => {
        const idx = cueNumber - 1;
        const text = cues[idx] ? String(cues[idx].content) : "";
        return (
          <div className="vb-row" key={cueNumber} style={{ animationDelay: `${rowIndex * 0.09}s` }}>
            <div
              className="vb-bar"
              style={{ backgroundColor: CUE_HEX[idx] ?? "#bbb", height: Math.round((mobile ? 32 : 31) * scale) }}
            >
              <span
                className="vb-bar-rank"
                style={{
                  color: RANK_COLORS[rowIndex],
                  WebkitTextStroke: `1px ${RANK_STROKES[rowIndex]}`,
                }}
              >
                {rowIndex + 1}
              </span>
              <span
                className="vb-bar-text"
                style={{ color: CUE_FG[idx] ?? "#fff", fontSize: barFontPx(text.length, !!mobile) * scale }}
              >
                {renderCueContent(text)}
              </span>
            </div>

            <div className={"vb-slots" + (asCurve ? " vb-slots--curve" : "")}>
              {/* The scale ends, marked per row: put it first → 1, put it last → 5. */}
              <span
                className="vb-side"
                style={{ color: RANK_COLORS[0], WebkitTextStroke: `0.6px ${RANK_STROKES[0]}` }}
              >
                1
              </span>
              {asCurve ? (
                <DistributionCurve
                  counts={slots.map(
                    (slot) =>
                      ranked.filter((p) => p.sequence.indexOf(cueNumber) + 1 === slot).length
                  )}
                  color={CUE_HEX[idx] ?? "#bbb"}
                  viewerSlot={viewerSlotOf(cueNumber)}
                  surface={SURFACE}
                  height={curveH}
                  scale={scale}
                />
              ) : (
              <div className="vb-cols">
              {slots.map((slot) => {
                const here = ranked.filter((p) => p.sequence.indexOf(cueNumber) + 1 === slot);
                return (
                  <div className="vb-col" key={slot}>
                    {here.length > 0 && (
                      <>
                        <div className="vb-avatars">
                          {here.map((p, i) => (
                            <div
                              key={p.id}
                              className={"vb-avatar" + (p.id === viewerId ? " vb-avatar--self" : "")}
                              style={{
                                marginLeft: i === 0 ? 0 : -overlapFor(here.length),
                                zIndex: here.length - i,
                                width: avatarPx,
                                height: avatarPx,
                              }}
                            >
                              <PlayerAvatar color={p.color} size={avatarPx} />
                            </div>
                          ))}
                        </div>
                        <div className="vb-names">{here.map((p) => p.name).join(", ")}</div>
                      </>
                    )}
                  </div>
                );
              })}
              </div>
              )}
              <span
                className="vb-side"
                style={{ color: RANK_COLORS[4], WebkitTextStroke: `0.6px ${RANK_STROKES[4]}` }}
              >
                5
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
